// src/firebase/tutorLinks.js
// Linked tutors (2026-09-28): one student account, several tutors, one of
// them "active" at a time. See the LINKED TUTORS section of
// functions/index.js for the data shape - in short, a linked student has
// `memberships: { [orgId]: {cohortId, cohortName, ..., joinedAt} }` plus
// derived `orgIds`/`cohortIds`, and their top-level orgId/cohortId/etc. are
// always the ACTIVE tutor's. Students without `memberships` (everyone before
// this feature) behave exactly as before - every helper here is a no-op for
// them.

import { doc, getDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { db } from './config';
import { getCurrentUserOrgId } from './orgs';

// Any change made through here may alter the linked students list, so it
// drops the short-lived copy kept by getLinkedStudentsForMyOrg below.
const call = (name, data) => {
  if (name !== 'getLinkedStudents') linkedStudentsCache = null;
  return httpsCallable(getFunctions(getApp()), name)(data).then(r => r.data);
};

export const isLinkedStudent = (data) => Object.keys(data?.memberships || {}).length > 1;

// A student as one tutor should see them: that tutor's cohort, suspension,
// extra time etc., and "createdAt" = when they joined that tutor (so a newly
// linked student shows up in that tutor's recent registrations).
export const studentViewForOrg = (data, orgId) => {
  const m = data?.memberships?.[orgId];
  if (!m) return data;
  const view = { ...data, ...m, orgId };
  if (m.joinedAt) view.createdAt = m.joinedAt;
  return view;
};

// Linked students of the caller's org who currently have ANOTHER tutor
// active - the existing orgId-based queries can't see them. Listed by the
// getLinkedStudents function (a list rule on orgIds can't be made safe),
// already in this org's view; `filter` applies the same conditions the
// caller's own query used (cohort, suspended...).
const fromJson = (v) => (v && typeof v === 'object' && '__ts' in v) ? Timestamp.fromMillis(v.__ts)
  : Array.isArray(v) ? v.map(fromJson)
  : (v && typeof v === 'object') ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, fromJson(x)])) : v;

// Opening the Cohorts tab asks for this list several times at once
// (registrations, counts, the results grid...), so callers within a few
// seconds of each other share one function call instead of each waiting
// on their own - which, with the function idle, meant several cold starts.
const LINKED_STUDENTS_REUSE_MS = 5000;
let linkedStudentsCache = null; // { orgId, at, promise }

export const getLinkedStudentsForMyOrg = async (filter = () => true) => {
  try {
    const orgId = await getCurrentUserOrgId();
    const cached = linkedStudentsCache;
    let promise;
    if (cached && cached.orgId === orgId && Date.now() - cached.at < LINKED_STUDENTS_REUSE_MS) {
      promise = cached.promise;
    } else {
      promise = call('getLinkedStudents', {}).then(({ students }) => students.map(fromJson));
      const entry = { orgId, at: Date.now(), promise };
      linkedStudentsCache = entry;
      promise.catch(() => { if (linkedStudentsCache === entry) linkedStudentsCache = null; });
    }
    return (await promise).filter(filter);
  } catch (error) {
    console.error('Get linked students error:', error);
    return [];
  }
};

// Merges a tutor-side student list with that tutor's linked students,
// applying the tutor's view to every row (a linked student who happens to
// have THIS tutor active appears in both - de-duplicated by id).
export const withLinkedStudents = async (students, filter) => {
  const orgId = await getCurrentUserOrgId();
  const byId = new Map(students.map(s => [s.id, studentViewForOrg(s, orgId)]));
  (await getLinkedStudentsForMyOrg(filter)).forEach(s => byId.set(s.id, s));
  return [...byId.values()];
};

// Tutor-side change to one student. Unlinked student: the same direct
// Firestore update as before. Linked student: goes through the
// updateLinkedStudent function so only this tutor's link changes.
// buildChanges(ts) gets the right "now" value for each path.
export const applyStudentChanges = async (studentId, buildChanges) => {
  const ref = doc(db, 'users', studentId);
  const snap = await getDoc(ref);
  if (snap.exists() && isLinkedStudent(snap.data())) {
    await call('updateLinkedStudent', { studentId, changes: buildChanges('__serverTimestamp__') });
  } else {
    await updateDoc(ref, buildChanges(serverTimestamp()));
  }
};

export const removeLinkedStudentFromOrg = (studentId) => call('removeLinkedStudentFromOrg', { studentId });

// ---- Student side ----

// Only the tutors this student is already linked to (never a platform-wide
// list) - [{ orgId, name, pending }]
export const getMyTutors = () => call('getMyTutors', {}).then(r => r.tutors);
// From the dashboard: the typed tutor name + cohort code. From a tutor's own
// registration link: that link's orgId + code.
export const joinTutor = ({ tutorName = '', orgId = null, cohortCode }) => call('joinTutorCohort', { tutorName, orgId, cohortCode });
export const switchTutor = (orgId) => call('switchActiveTutor', { orgId });

// An action to run once the student is signed in - set by the registration
// page ("log in to add this tutor") or an inbound ae-course-hosting sign-in
// that names the tutor to land on. sessionStorage, so it never outlives the
// tab.
const PENDING_KEY = 'pendingTutorAction';
export const setPendingTutorAction = (action) => {
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(action)); } catch (e) { /* storage blocked */ }
};

// Returns true if something changed (the caller should reload the profile).
export const runPendingTutorAction = async (profile) => {
  let action = null;
  try {
    action = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
    sessionStorage.removeItem(PENDING_KEY);
  } catch (e) { return false; }
  if (!action || profile?.role !== 'student') return false;
  try {
    if (action.type === 'join') {
      await joinTutor({ orgId: action.orgId, cohortCode: action.cohortCode });
      return true;
    }
    if (action.type === 'switch' && action.orgId && action.orgId !== profile.orgId && profile.memberships?.[action.orgId]) {
      await switchTutor(action.orgId);
      return true;
    }
  } catch (error) {
    console.error('Pending tutor action failed:', error);
    if (action.type === 'join') alert(error.message || 'Could not add that tutor. Please try again from your dashboard.');
  }
  return false;
};
